import { z } from "zod";
import { saleLineKindSchema } from "./types.js";
/**
 * Kampanya TANIMI. Tanımlar sunucuda saklanır (`campaigns` tablosu) ve ajanın ana verisiyle POS'a iner;
 * UYGULAMA bu paketteki saf fonksiyonlardadır (`applyCampaigns`) — POS'ta çevrimdışı, sunucuda doğrulama için aynı kod.
 *
 * Sürüm 1 (Prompt 15) bilinçli olarak dardı: üç etki türü, üç kapsam, zaman penceresi, asgari miktar / tutar, cari grubu.
 * Prompt 16 genişletmesi (ADR 0016 §17): `BUY_X_PAY_Y` etkisi, sepet eşiği (`minBasketGross` → "sepet kampanyası") ve
 * `LOYALTY_TIER` müşteri türü.
 * Prompt 17 genişletmesi (ADR 0017 §23, §26): `requiresCoupon` (işletmenin KENDİ kuponu = İNDİRİM; kampanya yalnızca kupon
 * kodu okutulunca uygulanır) ve `crossCondition` (aynı sepette ≥ X litre yakıt → yıkama indirimi; şarj sürerken → kafe
 * indirimi). Puan harcaması TAHSİLATTIR (`LOYALTY_POINTS`), kampanya değildir; üçüncü taraf çeki `COUPON` tahsilatıdır.
 *
 * **Geriye uyum:** yeni alanların hepsi varsayılanlıdır (`null` / `[]`). Go ajanı kampanyaları ham JSON taşır; eski sunucunun
 * ürettiği (yeni alanları hiç içermeyen) kayıt bu şemayla AYNEN ayrışır ve v1 davranışını verir.
 */
export const CAMPAIGN_EFFECT_TYPES = [
    "PERCENT_OFF",
    "AMOUNT_OFF",
    "FIXED_UNIT_PRICE",
    "BUY_X_PAY_Y",
];
export const campaignEffectTypeSchema = z.enum(CAMPAIGN_EFFECT_TYPES);
export const CAMPAIGN_EFFECT_TYPE_LABELS = {
    PERCENT_OFF: "Yüzde indirim",
    AMOUNT_OFF: "Tutar indirimi (birim başına)",
    FIXED_UNIT_PRICE: "Sabit birim fiyat",
    BUY_X_PAY_Y: "X al Y öde",
};
export const CAMPAIGN_SCOPE_KINDS = ["PRODUCT", "CATEGORY", "BUSINESS_UNIT"];
export const campaignScopeKindSchema = z.enum(CAMPAIGN_SCOPE_KINDS);
export const CAMPAIGN_CUSTOMER_KINDS = ["ANY", "IDENTIFIED", "FLEET", "LOYALTY_TIER"];
export const campaignCustomerKindSchema = z.enum(CAMPAIGN_CUSTOMER_KINDS);
export const CAMPAIGN_CUSTOMER_KIND_LABELS = {
    ANY: "Herkes",
    IDENTIFIED: "Tanınan müşteri (plaka / kart / cari)",
    FLEET: "Filo müşterisi",
    LOYALTY_TIER: "Sadakat seviyesi",
};
/**
 * Çapraz koşul (ADR 0017 §26):
 *  - `BASKET_LINE_KIND_MIN_QUANTITY` → AYNI sepette `lineKind` türündeki satırların toplam miktarı ≥ `minQuantity` olmalı
 *    (ör. "40 L üzeri yakıta yıkama %20": kapsam = WASH, koşul = FUEL ≥ 40).
 *  - `CONTEXT_FLAG` → bağlamda bayrak açık olmalı (`EV_CHARGING_ACTIVE`: müşterinin şarj oturumu sürüyor — Prompt 19 doldurur).
 */
export const CAMPAIGN_CONTEXT_FLAGS = ["EV_CHARGING_ACTIVE"];
export const campaignContextFlagSchema = z.enum(CAMPAIGN_CONTEXT_FLAGS);
export const CAMPAIGN_CONTEXT_FLAG_LABELS = {
    EV_CHARGING_ACTIVE: "Şarj oturumu sürüyor",
};
/** X al Y öde sınırları — `0018_market.sql` CHECK'leriyle AYNI: 1 ≤ Y < X ≤ 100. */
export const BUY_X_PAY_Y_LIMITS = { minBuy: 2, maxBuy: 100, minPay: 1 };
/** Sadakat seviye kodu: kırpılmış, 1–40 karakter; bir kampanyada en çok 20 seviye. */
export const LOYALTY_TIER_LIMITS = { maxLength: 40, maxTiers: 20 };
const percentSchema = z.string().regex(/^\d{1,2}(\.\d{1,2})?$|^100$/, "Yüzde 0–100, en çok 2 ondalık");
const kurusSchema = z.string().regex(/^\d{1,15}$/, "Kuruş, tam sayı dizge");
/** Sıfırdan büyük kuruş (sepet eşiği — veritabanı `min_basket_gross > 0` ister). */
// Yalnızca rakamdan oluşan dizge sıfırdan büyüktür ⇔ sıfır olmayan bir rakam içerir (düzen hatasında da güvenli).
const positiveKurusSchema = kurusSchema.refine((value) => /[1-9]/.test(value), "Tutar sıfırdan büyük olmalıdır");
const unitPriceSchema = z.string().regex(/^\d{1,15}(\.\d{1,4})?$/, "Birim fiyat, en çok 4 ondalık");
const quantitySchema = z.string().regex(/^\d{1,15}(\.\d{1,3})?$/, "Miktar, en çok 3 ondalık");
/** "HH:MM" yerel saat (Europe/Istanbul). */
const localTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
const buyQuantitySchema = z
    .number()
    .int("Tam sayı olmalıdır")
    .min(BUY_X_PAY_Y_LIMITS.minBuy, `En az ${BUY_X_PAY_Y_LIMITS.minBuy}`)
    .max(BUY_X_PAY_Y_LIMITS.maxBuy, `En çok ${BUY_X_PAY_Y_LIMITS.maxBuy}`);
const payQuantitySchema = z
    .number()
    .int("Tam sayı olmalıdır")
    .min(BUY_X_PAY_Y_LIMITS.minPay, `En az ${BUY_X_PAY_Y_LIMITS.minPay}`)
    .max(BUY_X_PAY_Y_LIMITS.maxBuy - 1, `En çok ${BUY_X_PAY_Y_LIMITS.maxBuy - 1}`);
const loyaltyTierSchema = z.string().trim().min(1).max(LOYALTY_TIER_LIMITS.maxLength);
export const campaignCrossConditionSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("BASKET_LINE_KIND_MIN_QUANTITY"),
        lineKind: saleLineKindSchema,
        minQuantity: quantitySchema.refine((value) => /[1-9]/.test(value), "Miktar sıfırdan büyük olmalıdır"),
    }),
    z.object({ kind: z.literal("CONTEXT_FLAG"), flag: campaignContextFlagSchema }),
]);
/**
 * Kampanya alanları — ARINDIRILMAMIŞ nesne şeması (çapraz alan kuralı YOK). `extend` / `omit` ile türetilen API
 * şemaları bunu kullanır ve kuralı `withCampaignRules` ile yeniden bağlar (Zod 4 kurallı nesnede `omit`'e izin vermez).
 * Doğrudan ayrıştırma için `campaignSchema` kullanılmalıdır.
 */
export const campaignObjectSchema = z.object({
    id: z.uuid(),
    code: z.string().min(2).max(40),
    name: z.string().min(1).max(120),
    /** Öncelik: küçük sayı önce uygulanır; aynı satıra birden çok kampanya uygulanmaz (ilk eşleşen kazanır). */
    priority: z.number().int().min(0).max(1000).default(100),
    scopeKind: campaignScopeKindSchema,
    /** `PRODUCT` → ürün kimlikleri; `CATEGORY` → kategori kimlikleri; `BUSINESS_UNIT` → iş birimi kodları. */
    scopeIds: z.array(z.string().min(1)).min(1).max(500),
    /** Boş dizi = şirketin tüm istasyonları. */
    stationIds: z.array(z.uuid()).default([]),
    effectType: campaignEffectTypeSchema,
    /**
     * `PERCENT_OFF` → yüzde; `AMOUNT_OFF` → birim başına kuruş; `FIXED_UNIT_PRICE` → birim fiyat (kuruş ya da ₺/L 4 ondalık).
     * `BUY_X_PAY_Y` → motor KULLANMAZ (etki `buyQuantity` / `payQuantity`'dedir); sunucu okunur ayna olarak "X/Y" yazar.
     */
    effectValue: z.string().min(1),
    /** `BUY_X_PAY_Y` iken ZORUNLU, diğer türlerde `null`: her X birimde… */
    buyQuantity: buyQuantitySchema.nullable().default(null),
    /** …Y birimin bedeli ödenir (1 ≤ Y < X). */
    payQuantity: payQuantitySchema.nullable().default(null),
    minQuantity: quantitySchema.nullable().default(null),
    minLineGross: kurusSchema.nullable().default(null),
    /**
     * Sepet eşiği (kuruş). DOLU ise kampanya "sepet kampanyası"dır: satır kampanyalarından SONRA, indirimli sepet brütü
     * bu tutara ulaşınca, satır kampanyası almamış kapsamdaki satırlara uygulanır (ADR 0016 §17; `applyCampaigns`).
     */
    minBasketGross: positiveKurusSchema.nullable().default(null),
    customerKind: campaignCustomerKindSchema.default("ANY"),
    /** `LOYALTY_TIER` iken en az bir seviye kodu (birebir, büyük / küçük harf duyarlı); diğer türlerde boş. */
    loyaltyTiers: z.array(loyaltyTierSchema).max(LOYALTY_TIER_LIMITS.maxTiers).default([]),
    validFrom: z.iso.datetime({ offset: true }),
    validTo: z.iso.datetime({ offset: true }).nullable().default(null),
    /** Haftanın günleri (0 = Pazar … 6 = Cumartesi); boş = her gün. */
    weekdays: z.array(z.number().int().min(0).max(6)).default([]),
    /** Yerel saat penceresi; ikisi de boşsa gün boyu. `timeTo < timeFrom` gece yarısını aşar (22:00–06:00). */
    timeFrom: localTimeSchema.nullable().default(null),
    timeTo: localTimeSchema.nullable().default(null),
    isActive: z.boolean().default(true),
    /** true → kampanya YALNIZCA bağlamdaki kupon kampanyaları arasındaysa uygulanır (`CampaignContext.couponCampaignIds`). */
    requiresCoupon: z.boolean().default(false),
    crossCondition: campaignCrossConditionSchema.nullable().default(null),
});
/** Etki türüne göre `effectValue` düzeni (`BUY_X_PAY_Y`'de değer kullanılmaz → düzen yok). */
const EFFECT_VALUE_SCHEMAS = {
    PERCENT_OFF: percentSchema,
    AMOUNT_OFF: kurusSchema,
    FIXED_UNIT_PRICE: unitPriceSchema,
};
/**
 * Çapraz alan kuralları (veritabanı CHECK'lerinin TS eşleniği — `0018_market.sql` §10):
 *  - `effectValue` etki türünün düzenine uyar (yüzde / kuruş / birim fiyat). Uymayan değer motorda hesap hatası
 *    doğururdu; POS böyle bir tanımı atlar (satışı düşürmez), sunucu 400 döner.
 *  - `BUY_X_PAY_Y` ⇔ `buyQuantity` ve `payQuantity` dolu; ikisi doluysa `payQuantity < buyQuantity`.
 *  - `LOYALTY_TIER` ⇔ en az bir sadakat seviyesi.
 * Boş dizi = kural sağlanıyor. Sunucu (güncellemede birleştirilmiş kayıt), web formu ve şema AYNI fonksiyonu kullanır.
 */
export function campaignRuleIssues(input) {
    const issues = [];
    if (input.effectType === "BUY_X_PAY_Y") {
        if (input.buyQuantity === null)
            issues.push({ path: "buyQuantity", message: "X al Y öde için alınan adet (X) zorunludur" });
        if (input.payQuantity === null)
            issues.push({ path: "payQuantity", message: "X al Y öde için ödenen adet (Y) zorunludur" });
        if (input.buyQuantity !== null && input.payQuantity !== null && input.payQuantity >= input.buyQuantity)
            issues.push({ path: "payQuantity", message: "Ödenen adet (Y) alınan adetten (X) küçük olmalıdır" });
    }
    else {
        const parsed = EFFECT_VALUE_SCHEMAS[input.effectType].safeParse(input.effectValue);
        if (!parsed.success)
            issues.push({
                path: "effectValue",
                message: parsed.error.issues[0]?.message ?? "Geçersiz etki değeri",
            });
        if (input.buyQuantity !== null)
            issues.push({ path: "buyQuantity", message: "Alınan adet yalnızca X al Y öde kampanyasında girilir" });
        if (input.payQuantity !== null)
            issues.push({ path: "payQuantity", message: "Ödenen adet yalnızca X al Y öde kampanyasında girilir" });
    }
    if (input.customerKind === "LOYALTY_TIER") {
        if (input.loyaltyTiers.length === 0)
            issues.push({
                path: "loyaltyTiers",
                message: "Sadakat seviyesi kampanyasında en az bir seviye girilmelidir",
            });
    }
    else if (input.loyaltyTiers.length > 0) {
        issues.push({
            path: "loyaltyTiers",
            message: "Sadakat seviyeleri yalnızca müşteri türü 'Sadakat seviyesi' iken girilir",
        });
    }
    return issues;
}
/** Çapraz alan kurallarını bir nesne şemasına bağlar (API'nin `extend` / `omit` ile türettiği şemalar için). */
export function withCampaignRules(schema) {
    return schema.superRefine((value, context) => {
        for (const issue of campaignRuleIssues(value))
            context.addIssue({ code: "custom", path: [issue.path], message: issue.message });
    });
}
/** Kampanya şeması: alanlar + çapraz alan kuralları. POS ana veriyi, sunucu girdiyi bununla doğrular. */
export const campaignSchema = withCampaignRules(campaignObjectSchema);
/** Sepet kampanyası mı? (`minBasketGross` dolu → evet; ADR 0016 §17.) */
export function isBasketCampaign(campaign) {
    return campaign.minBasketGross !== null && campaign.minBasketGross !== undefined;
}
export { percentSchema as campaignPercentSchema, kurusSchema as campaignKurusSchema, positiveKurusSchema as campaignPositiveKurusSchema, unitPriceSchema as campaignUnitPriceSchema, buyQuantitySchema as campaignBuyQuantitySchema, payQuantitySchema as campaignPayQuantitySchema, loyaltyTierSchema as campaignLoyaltyTierSchema, };
//# sourceMappingURL=campaign.js.map